import React, { useCallback, useContext, useEffect, useState } from 'react';
import { shouldFetchData, status } from '../../../../data/fetchStatus';
import { ExpertContext } from '../../../Contexts/contexts';
import Services from '../../../Services/services';
import QuoteElementStripePaiment from './QuoteElementStripePaiment';
import BillComponent from './BillComponent/BillComponent';
import { useSearchParams } from 'react-router-dom';
import { formatDate, getPaymentDate } from '../../../../utils/date';
import ConfirmationPaiementModal from './ConfirmationPaiementModal';
import {
  floatKeyPress,
  floatValueChanged,
} from '../../../../utils/InputVerifications';
import useToast from '../../../Toasts/useToast';
import {
  ToastHeaders,
  ToastThemes,
  ToastMessages,
} from '../../../Toasts/ToastOptions/toastOptions';

function QuoteElementExpertPaiementModal(props) {
  const expertContext = useContext(ExpertContext);

  const [searchParams, setSearchParams] = useSearchParams();

  // useState pour montrer ou cacher la modal
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [files, setFiles] = useState({});

  const enqueueToast = useToast();

  // useState pour le montant de la facture
  const [amountBill, setAmountBill] = useState(0);

  const [expertCommission, setExpertCommission] = useState(undefined);
  const [expertCommissonFetched, setExpertCommissionFetched] = useState(
    status.DEFAULT
  );

  const [paramsFetched, setParamsFetched] = useState(status.DEFAULT);

  const [paymentLink, setPaymentLink] = useState('');

  const [loader, setLoader] = useState({
    loadCommission: true,
    loadParams: true,
  });

  function closeModal(e) {
    e.stopPropagation();
    setShowModal(false);
  }

  // #region Calculs commission

  function commission() {
    return parseFloat(amountBill * 0.08).toFixed(2);
  }

  function commissionTTC() {
    return parseFloat(commission(amountBill) * 1.2).toFixed(2);
  }

  // #endregion

  async function validate() {
    if (
      !Object.keys(files).length &&
      expertCommission?.paymentPeriod !== 'immediat'
    )
      return;

    if (parseFloat(amountBill).toFixed(2) < 5.5) return;

    setLoader((prevState) => ({
      ...prevState,
      loadParams: false,
      loadCommission: true,
    }));

    try {
      let expertInvoices = {};

      if (expertCommission?.paymentPeriod !== 'immediat') {
        if (Object.keys(files).length > 0) {
          const fileNames = [];

          for (const [, value] of Object.entries(files))
            fileNames.push(`${Date.now()}_${value.name.replace(/\s/g, '')}`);

          const uploadLinks =
            await Services.documentService.generateUploadLinks(fileNames);

          const promises = [];

          for (const [idx, value] of Object.entries(files)) {
            promises.push(
              Services.documentService.uploadFile(value.file, uploadLinks[idx])
            );
          }

          await Promise.all(promises);

          promises.splice(0, promises.length);

          for (const [idx, value] of Object.entries(files)) {
            promises.push(
              Services.documentService.createDocumentUploaded(
                value.name,
                fileNames[idx],
                'c4397611-7f9c-4e50-bbc2-e63bc2fabb4b',
                props?.clientExpertRelation?.clientId,
                props?.clientExpertRelation?.expertId,
                'expert',
                'expert invoice'
              )
            );
          }

          const documentsUploaded = await Promise.all(promises);

          for (let i = 0; i < documentsUploaded.length; i++) {
            expertInvoices[i] = documentsUploaded[i].id;
          }
        }
      }

      await Services.profileService.createExpertInvoice(
        expertContext.expert.id,
        expertCommission.id,
        parseInt(amountBill * 100),
        expertInvoices
      );

      setExpertCommissionFetched(status.REFRESH);
    } catch (error) {
      console.error(error);
      setLoader((prevState) => ({
        ...prevState,
        loadParams: false,
        loadCommission: false,
      }));
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
    }
  }

  // #region Fetch and UseEffect

  const fetchExpertCommission = useCallback(async () => {
    if (
      expertCommissonFetched === status.FETCHED ||
      props?.clientExpertRelation?.expertCommission?.id === undefined
    )
      return;

    setExpertCommissionFetched(status.FETCHED);

    try {
      const expertCommissionData =
        await Services.profileService.expertInvoiceByCommissionId(
          props?.clientExpertRelation?.expertCommission?.id
        );

      setExpertCommission(expertCommissionData);

      const invoiceLinks = expertCommissionData?.expertInvoices?.filter(
        (invoice) => invoice.stripePaymentLink !== undefined
      );

      if (invoiceLinks.length > 0) {
        setPaymentLink(invoiceLinks[0].stripePaymentLink);
        setAmountBill(invoiceLinks[0].clientAmount / 100);
      } else {
        setPaymentLink('');
      }

      setLoader((prevState) => ({
        ...prevState,
        loadCommission: false,
      }));
    } catch (error) {
      console.error(error);
      setLoader((prevState) => ({
        ...prevState,
        loadCommission: false,
      }));
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
    }
  }, [
    expertCommissonFetched,
    props?.clientExpertRelation?.expertCommission?.id,
  ]);

  useEffect(() => {
    if (shouldFetchData(expertCommissonFetched)) fetchExpertCommission();
  }, [expertCommissonFetched, fetchExpertCommission]);

  useEffect(() => {
    async function VerifyPaymentIntentState() {
      if (
        expertCommissonFetched === status.FETCHED &&
        expertCommission !== undefined &&
        shouldFetchData(paramsFetched)
      ) {
        setParamsFetched(status.FETCHED);

        if (
          searchParams.has('redirect_status') &&
          searchParams.get('redirect_status') === 'succeeded'
        ) {
          if (searchParams.has('payment_intent')) {
            const invoice = expertCommission?.expertInvoices?.filter(
              (invoice) =>
                invoice.stripePaymentIntentId ===
                searchParams.get('payment_intent')
            );

            if (invoice !== undefined && !invoice.length) return;

            try {
              props?.showDetails();
              //setShowModal(true);

              await Services.profileService.expertInvoicePayd(invoice[0].id);

              await Services.profileService.expertUpdatePayment(
                expertCommission.id
              );

              setLoader((prevState) => ({
                ...prevState,
                loadParams: false,
                loadCommission: true,
              }));
              setAmountBill(0);
              setExpertCommissionFetched(status.REFRESH);
              setShowConfirmModal(true);
              setSearchParams({});
            } catch (error) {
              console.error(error);
              setLoader((prevState) => ({
                ...prevState,
                loadParams: false,
                loadCommission: false,
              }));
              enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
                theme: ToastThemes.toastThemes.ERROR_OCCURED,
                header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
                duration: 3000,
              });
            }
          }
        } else {
          setLoader((prevState) => ({
            ...prevState,
            loadParams: false,
          }));
        }
      }
    }
    VerifyPaymentIntentState();
  }, [
    searchParams,
    paramsFetched,
    setParamsFetched,
    setSearchParams,
    expertCommission,
    expertContext.expert,
    expertCommissonFetched,
    setExpertCommissionFetched,
    props,
  ]);

  //#endregion

  function getPeriod(period) {
    return `${formatDate(
      getPaymentDate(period, expertCommission.nextAt, false),
      'D MMMM YYYY'
    )} - ${formatDate(expertCommission.nextAt, 'D MMMM YYYY')}`;
  }

  const handleValueChanged = (e) => {
    floatValueChanged(e, null, (value) => setAmountBill(value));
  };

  const handleKeyPress = (e) => {
    floatKeyPress(e, null);
  };

  const modalClasses = () => {
    return `modal-paiement modal-paiement-large modal-paiement-${expertCommission?.paymentPeriod}`;
  };

  return (
    <>
      {expertCommission !== undefined &&
        expertCommission.finishedAt === null && (
          <>
            <div className="container-client-details">
              <div className="buttons-container">
                <div
                  className="element-button pending-status"
                  onClick={() => setShowModal(true)}
                >
                  {expertCommission?.paymentPeriod === 'immediat'
                    ? 'Payer la commission'
                    : `Payer la commission pour la période du ${formatDate(
                        getPaymentDate(
                          expertCommission?.paymentPeriod,
                          expertCommission.nextAt,
                          false
                        ),
                        'D MMMM YYYY'
                      )} au ${formatDate(
                        expertCommission.nextAt,
                        'D MMMM YYYY'
                      )}`}
                </div>
              </div>
            </div>
            {showModal && (
              <div className="container-rdv-modal">
                <div
                  className="background-rdv-modal"
                  onClick={(e) => closeModal(e)}
                ></div>
                <div className={modalClasses()}>
                  <div className="modal-paiement-upper-part-step2">
                    <div className="modal-paiement-title-progression-bar">
                      <div className="modal-paiement-title-details-container">
                        <div className="modal-paiement-title-main">
                          Paiement de la commission CEC
                        </div>
                        <div className="modal-paiement-details">
                          {expertCommission?.paymentPeriod === 'immediat'
                            ? 'Paiement de la commission pour le client sélectionné'
                            : `Veuillez s'il vous plaît nous fournir les factures correspondantes au client que nous vous avons apporté pour la période suivante : ${getPeriod(
                                expertCommission?.paymentPeriod
                              )}`}
                        </div>
                      </div>
                    </div>
                    <div className="modal-paiement-client-details-container">
                      <div className="modal-paiement-client-number modal-margin-paiement-client-number">
                        <div className="modal-paiement-title">Nom client</div>
                        <div className="client-name">
                          {props?.clientExpertRelation?.client?.firstName}{' '}
                          {props?.clientExpertRelation?.client?.lastName}
                        </div>
                      </div>
                      <div className="modal-paiement-client-number modal-margin-paiement-client-number">
                        <div className="modal-paiement-title">
                          Numéro de dossier
                        </div>
                        <div className="client-number">
                          #{props?.clientExpertRelation?.referenceNumber}
                        </div>
                      </div>
                      <div className="modal-paiement-title">
                        Votre périodicité de paiement configurée :
                        <span className="frequency-paiement">
                          {expertCommission?.paymentPeriod}
                        </span>
                      </div>
                    </div>
                  </div>
                  {!loader.loadCommission && !loader.loadParams ? (
                    <div className="modal-paiement-bottom-part-step2">
                      {expertCommission === undefined ||
                        (!paymentLink.length &&
                          expertCommission?.paymentPeriod !== 'immediat' && (
                            <>
                              <div className="modal-paiement-left-part scroller">
                                <BillComponent
                                  files={files}
                                  setFiles={(incommingfiles) =>
                                    setFiles(incommingfiles)
                                  }
                                />
                              </div>
                              <div className="modal-paiement-separator"></div>
                            </>
                          ))}
                      {paymentLink.length > 0 && (
                        <>
                          <QuoteElementStripePaiment
                            paymentLink={paymentLink}
                          />
                          <div className="modal-paiement-separator"></div>
                        </>
                      )}
                      <div className="modal-paiement-right-part">
                        <div className="modal-paiement-title">
                          Portail de paiement
                        </div>
                        <div className="modal-paiement-group-inputs">
                          <label className="modal-paiement-label">
                            Montant facturé au client
                          </label>
                          <input
                            type="number"
                            className="modal-paiement-input amount"
                            name="amount"
                            id="amount"
                            value={amountBill}
                            onChange={handleValueChanged}
                            onKeyDown={handleKeyPress}
                            disabled={paymentLink.length > 0}
                          ></input>
                        </div>
                        <div className="modal-paiement-group-inputs">
                          <label className="modal-paiement-label">
                            Commission CEC HT
                          </label>
                          <div className="modal-paiement-input commission">
                            {commission()}
                          </div>
                        </div>
                        <div className="modal-paiement-group-inputs">
                          <label className="modal-paiement-label">
                            Commission TTC
                          </label>
                          <div className="modal-paiement-input commissionTTC">
                            {commissionTTC()}
                          </div>
                        </div>
                        {/* <div className="modal-paiement-cancel-btn dl-bill">
									Télécharger la facture de commission CEC
								</div> */}
                        {!paymentLink.length && (
                          <div className="modal-paiement-buttons-container">
                            <div
                              className="modal-paiement-next-step-btn"
                              onClick={() => validate()}
                            >
                              Procéder au paiement
                            </div>
                            <div
                              className="modal-paiement-cancel-btn"
                              onClick={() => setShowModal(false)}
                            >
                              Annuler
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="modal-paiement-loader-container">
                      <div className="modal-paiement-loader"></div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {showConfirmModal && (
              <ConfirmationPaiementModal
                hideModal={() => setShowConfirmModal(false)}
              />
            )}
          </>
        )}
    </>
  );
}

export default QuoteElementExpertPaiementModal;
